// ════════════════════════════════════════════════════════════════════════════
// EditEventRequest — заявка владельца на редактирование уже опубликованного
// ивента. Принципиально отличается от CreateEvents: ничего не меняет сразу,
// шлёт diff в /api/events/{id}/edit-request на модерацию.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { useParams, useNavigate } from "react-router-dom";
import { createToast } from "../../hooks/useToast";
import { API_URL } from "../../config";

const T = {
  en: {
    header:        "Edit Request",
    subheader:     "Submit changes for moderator review — your event stays live until approved.",
    pendingBanner: "You already have a pending edit request for this event. Submitting a new one will replace it.",
    cancelPending: "Withdraw pending request",
    pendingCanceled: "Pending request withdrawn.",
    rejectedBanner: "Your last edit request was rejected:",
    sectionBasic:  "Basic info",
    sectionWhen:   "When",
    sectionWhere:  "Where",
    sectionCover:  "Cover image",
    sectionPrice:  "Pricing & capacity",
    sectionSettings: "Settings",
    title:         "Event title",
    description:   "Description",
    date:          "Date",
    time:          "Time",
    location:      "Address",
    locationPlaceholder: "Search address or click on the map",
    chooseCover:   "Replace cover image",
    currentCover:  "Current cover",
    newCover:      "New cover (will replace current)",
    removeNewCover: "Remove",
    ticketPrice:   "Ticket price",
    free:          "Free event",
    currency:      "Currency",
    capacity:      "Capacity (optional)",
    isPrivate:     "Private event",
    allowInvites:  "Allow user invitations",
    allowMatching: "External matching",
    requirePhone:  "Require participant phone",
    organizerName: "Organizer name",
    organizerContact: "Contact",
    organizerWebsite: "Website",
    submit:        "Send for review",
    submitting:    "Sending…",
    cancel:        "Cancel",
    noChanges:     "Nothing to send — no fields changed.",
    sent:          "Edit request sent. A moderator will review it.",
    sentFail:      "Failed to send edit request",
    invalidImage:  "Image type not supported (PNG, JPG, WEBP).",
    fileTooBig:    "Image is too big (max 5 MB).",
    loadFail:      "Failed to load event.",
    notOwner:      "You don't have permission to edit this event.",
    backToEvent:   "Back to event",
  },
  ru: {
    header:        "Заявка на редактирование",
    subheader:     "Изменения отправляются на модерацию — ивент работает как обычно до одобрения.",
    pendingBanner: "У вас уже есть заявка на редактирование этого ивента. Новая заменит её.",
    cancelPending: "Отозвать заявку",
    pendingCanceled: "Заявка отозвана.",
    rejectedBanner: "Прошлая заявка была отклонена:",
    sectionBasic:  "Основное",
    sectionWhen:   "Когда",
    sectionWhere:  "Где",
    sectionCover:  "Обложка",
    sectionPrice:  "Цена и вместимость",
    sectionSettings: "Настройки",
    title:         "Название",
    description:   "Описание",
    date:          "Дата",
    time:          "Время",
    location:      "Адрес",
    locationPlaceholder: "Найти адрес или кликнуть на карте",
    chooseCover:   "Заменить обложку",
    currentCover:  "Текущая обложка",
    newCover:      "Новая обложка (заменит текущую)",
    removeNewCover: "Удалить",
    ticketPrice:   "Стоимость билета",
    free:          "Бесплатный",
    currency:      "Валюта",
    capacity:      "Вместимость (опц.)",
    isPrivate:     "Приватный ивент",
    allowInvites:  "Разрешить приглашения",
    allowMatching: "Внешний матчинг",
    requirePhone:  "Требовать телефон",
    organizerName: "Имя организатора",
    organizerContact: "Контакт",
    organizerWebsite: "Сайт",
    submit:        "Отправить на модерацию",
    submitting:    "Отправка…",
    cancel:        "Отмена",
    noChanges:     "Ничего не изменилось.",
    sent:          "Заявка отправлена. Модератор её рассмотрит.",
    sentFail:      "Не удалось отправить заявку",
    invalidImage:  "Неподдерживаемый формат (PNG, JPG, WEBP).",
    fileTooBig:    "Файл слишком большой (макс. 5 МБ).",
    loadFail:      "Не удалось загрузить ивент.",
    notOwner:      "Нет прав на редактирование.",
    backToEvent:   "Вернуться к ивенту",
  },
  fr: {
    header:        "Demande de modification",
    subheader:     "Vos modifications sont soumises à un modérateur — l'événement reste actif jusqu'à approbation.",
    pendingBanner: "Vous avez déjà une demande en attente. La nouvelle remplacera l'ancienne.",
    cancelPending: "Retirer la demande",
    pendingCanceled: "Demande retirée.",
    rejectedBanner: "Votre dernière demande a été refusée :",
    sectionBasic:  "Informations",
    sectionWhen:   "Quand",
    sectionWhere:  "Où",
    sectionCover:  "Couverture",
    sectionPrice:  "Tarif & capacité",
    sectionSettings: "Paramètres",
    title:         "Titre",
    description:   "Description",
    date:          "Date",
    time:          "Heure",
    location:      "Adresse",
    locationPlaceholder: "Chercher ou cliquer sur la carte",
    chooseCover:   "Remplacer la couverture",
    currentCover:  "Couverture actuelle",
    newCover:      "Nouvelle couverture (remplace l'actuelle)",
    removeNewCover: "Supprimer",
    ticketPrice:   "Prix",
    free:          "Gratuit",
    currency:      "Devise",
    capacity:      "Capacité (opt.)",
    isPrivate:     "Privé",
    allowInvites:  "Autoriser les invitations",
    allowMatching: "Matching externe",
    requirePhone:  "Téléphone requis",
    organizerName: "Organisateur",
    organizerContact: "Contact",
    organizerWebsite: "Site",
    submit:        "Envoyer pour révision",
    submitting:    "Envoi…",
    cancel:        "Annuler",
    noChanges:     "Aucune modification.",
    sent:          "Demande envoyée. Un modérateur l'examinera.",
    sentFail:      "Échec d'envoi",
    invalidImage:  "Type d'image non pris en charge (PNG, JPG, WEBP).",
    fileTooBig:    "Image trop volumineuse (max 5 Mo).",
    loadFail:      "Échec de chargement.",
    notOwner:      "Vous n'êtes pas propriétaire.",
    backToEvent:   "Retour à l'événement",
  },
};

function toast(m, t = "info") { createToast(m, t, 5000); }

export default function EditEventRequest({ lang = "en", theme = "dark", token }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const t = T[lang] || T.en;
  const isLight = theme === "light";

  const C = isLight
    ? { bg:"#f6f7fb", fg:"#1e293b", card:"#ffffff", border:"rgba(99,102,241,0.18)",
        inputBg:"#f0f2ff", primary:"#6366f1", accent:"#f59e0b",
        muted:"#64748b", warn:"#fef3c7", warnBorder:"#f59e0b",
        danger:"#dc2626", dangerBg:"#fee2e2" }
    : { bg:"#0d1525", fg:"#e2e8f0", card:"#152035", border:"rgba(148,163,252,0.13)",
        inputBg:"rgba(15,28,55,0.7)", primary:"#6366f1", accent:"#f59e0b",
        muted:"#94a3b8", warn:"rgba(245,158,11,0.13)", warnBorder:"#f59e0b",
        danger:"#f87171", dangerBg:"rgba(239,68,68,0.13)" };

  const [loading, setLoading]   = useState(true);
  const [event, setEvent]       = useState(null);
  const [active, setActive]     = useState(null); // существующая pending-заявка
  const [lastRejected, setLastRejected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Форма (плоская — поля published_event)
  const [form, setForm] = useState(null);
  const [position, setPosition] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  // Address autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAddressSelected, setIsAddressSelected] = useState(true);
  const mapRef = useRef(null);
  const locationInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // ── Загрузка ивента + проверка прав + проверка активной заявки ───────────
  useEffect(() => {
    if (!token || !eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/published_events/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error("load_fail");
        const data = await r.json();
        if (!data.is_mine) {
          if (!cancelled) {
            toast(t.notOwner, "error");
            navigate(`/events/${data.slug || eventId}`);
          }
          return;
        }
        if (cancelled) return;

        setEvent(data);
        setForm({
          title: data.title || "",
          description: data.description || "",
          location: data.location || "",
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          event_date: data.event_date?.split("T")[0] || "",
          event_time: (data.event_time || "").slice(0, 5),
          ticket_price: data.ticket_price?.toString() || "",
          currency: data.currency || "EUR",
          is_free: !data.ticket_price || data.ticket_price === 0,
          organizer_name: data.organizer_name || "",
          organizer_contact: data.organizer_contact || "",
          organizer_website: data.organizer_website || "",
          is_private: !!data.is_private,
          allow_user_invitations: data.allow_user_invitations !== false,
          require_participant_phone: !!data.require_participant_phone,
          capacity: data.capacity?.toString() || "",
          allow_external_matching: !!data.allow_external_matching,
        });
        if (data.latitude && data.longitude) {
          setPosition({ latitude: data.latitude, longitude: data.longitude });
        }
        if (data.event_cover_url) setCoverPreview(data.event_cover_url);

        // Активная заявка?
        try {
          const ar = await fetch(`${API_URL}/api/events/${eventId}/edit-request`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (ar.ok) {
            const ad = await ar.json();
            if (!cancelled) setActive(ad.active || null);
          }
        } catch { /* swallow */ }
      } catch {
        if (!cancelled) {
          toast(t.loadFail, "error");
          navigate("/events");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, token]);

  // Address search debounced
  useEffect(() => {
    if (!form?.location || isAddressSelected) return;
    const id = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(form.location)}&limit=5&addressdetails=1&accept-language=${lang === "fr" ? "fr" : "en"}`,
          { headers: { "User-Agent": "EditEventRequest/1.0" } },
        );
        const d = await r.json();
        setSuggestions(d);
        setShowSuggestions(d.length > 0);
      } catch { setSuggestions([]); setShowSuggestions(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [form?.location, isAddressSelected, lang]);

  useEffect(() => {
    const h = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          locationInputRef.current && !locationInputRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!position) return;
    const id = requestAnimationFrame(() => {
      mapRef.current?.jumpTo({
        center: [position.longitude, position.latitude],
        zoom: 16,
      });
    });
    return () => cancelAnimationFrame(id);
  }, [position]);

  // ── Сборка diff (только изменённые поля) ─────────────────────────────────
  const diff = useMemo(() => {
    if (!event || !form) return {};
    const d = {};
    const cur = {
      title: event.title || "",
      description: event.description || "",
      location: event.location || "",
      latitude: event.latitude ?? null,
      longitude: event.longitude ?? null,
      event_date: event.event_date?.split("T")[0] || "",
      event_time: (event.event_time || "").slice(0, 5),
      ticket_price: event.ticket_price ?? 0,
      currency: event.currency || "EUR",
      organizer_name: event.organizer_name || "",
      organizer_contact: event.organizer_contact || "",
      organizer_website: event.organizer_website || "",
      is_private: !!event.is_private,
      allow_user_invitations: event.allow_user_invitations !== false,
      require_participant_phone: !!event.require_participant_phone,
      capacity: event.capacity ?? null,
      allow_external_matching: !!event.allow_external_matching,
    };

    // Числовые / координаты с подменой из position
    const newLat = position?.latitude ?? form.latitude;
    const newLon = position?.longitude ?? form.longitude;
    const newPrice = form.is_free ? 0 : parseFloat(form.ticket_price || "0");
    const newCap = form.capacity ? parseInt(form.capacity, 10) : null;

    if ((form.title || "").trim() !== cur.title) d.title = form.title.trim();
    if ((form.description || "").trim() !== cur.description) d.description = form.description.trim();
    if ((form.location || "").trim() !== cur.location) d.location = form.location.trim();
    if (newLat !== cur.latitude) d.latitude = newLat;
    if (newLon !== cur.longitude) d.longitude = newLon;
    if (form.event_date !== cur.event_date) d.event_date = form.event_date;
    if (form.event_time !== cur.event_time) d.event_time = form.event_time;
    if (Math.abs(newPrice - Number(cur.ticket_price || 0)) > 1e-9) d.ticket_price = newPrice;
    if (form.currency !== cur.currency) d.currency = form.currency;
    if ((form.organizer_name || "").trim() !== cur.organizer_name) d.organizer_name = form.organizer_name.trim();
    if ((form.organizer_contact || "").trim() !== cur.organizer_contact) d.organizer_contact = form.organizer_contact.trim();
    if ((form.organizer_website || "").trim() !== (cur.organizer_website || "")) d.organizer_website = (form.organizer_website || "").trim();
    if (form.is_private !== cur.is_private) d.is_private = form.is_private;
    if (form.allow_user_invitations !== cur.allow_user_invitations) d.allow_user_invitations = form.allow_user_invitations;
    if (form.require_participant_phone !== cur.require_participant_phone) d.require_participant_phone = form.require_participant_phone;
    if (newCap !== cur.capacity) d.capacity = newCap;
    if (form.allow_external_matching !== cur.allow_external_matching) d.allow_external_matching = form.allow_external_matching;

    return d;
  }, [event, form, position]);

  const changedFieldsCount = Object.keys(diff).length + (coverFile ? 1 : 0);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const setField = (name, value) => setForm(p => ({ ...p, [name]: value }));

  const onCoverChoose = (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type?.startsWith("image/")) { toast(t.invalidImage, "error"); return; }
    if (f.size > 5 * 1024 * 1024) { toast(t.fileTooBig, "error"); return; }
    setCoverFile(f);
    if (coverPreview && coverPreview.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    setCoverPreview(URL.createObjectURL(f));
  };

  const onRemoveNewCover = () => {
    if (coverPreview && coverPreview.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    setCoverFile(null);
    setCoverPreview(event?.event_cover_url || null);
  };

  const onLocationSelect = (addr) => {
    const lat = parseFloat(addr.lat), lon = parseFloat(addr.lon);
    if (!isFinite(lat) || !isFinite(lon)) return;
    setField("location", addr.display_name);
    setPosition({ latitude: lat, longitude: lon });
    setSuggestions([]); setShowSuggestions(false); setIsAddressSelected(true);
  };

  const onMapClick = async (e) => {
    const pos = { longitude: e.lngLat.lng, latitude: e.lngLat.lat };
    setPosition(pos); setIsAddressSelected(false);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.latitude}&lon=${pos.longitude}&zoom=18&addressdetails=1&accept-language=${lang === "fr" ? "fr" : "en"}`,
        { headers: { "User-Agent": "EditEventRequest/1.0" } },
      );
      const d = await r.json();
      setField("location", d?.display_name || "Selected on map");
      setIsAddressSelected(true);
    } catch {
      setField("location", "Selected on map");
      setIsAddressSelected(true);
    }
  };

  const onWithdrawPending = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/events/${eventId}/edit-request`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      setActive(null);
      toast(t.pendingCanceled, "success");
    } catch {
      toast(t.sentFail, "error");
    }
  }, [eventId, token, t]);

  const onSubmit = async (ev) => {
    ev?.preventDefault();
    if (changedFieldsCount === 0) { toast(t.noChanges, "info"); return; }
    setSubmitting(true);
    try {
      const body = new FormData();
      Object.entries(diff).forEach(([k, v]) => {
        if (v === null || v === undefined) {
          body.append(k, "");
        } else if (typeof v === "boolean") {
          body.append(k, v ? "true" : "false");
        } else {
          body.append(k, String(v));
        }
      });
      if (coverFile) body.append("cover", coverFile);

      const r = await fetch(`${API_URL}/api/events/${eventId}/edit-request`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      if (!r.ok) {
        let msg = t.sentFail;
        try { const d = await r.json(); if (d.detail) msg = typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail); } catch {}
        throw new Error(msg);
      }
      toast(t.sent, "success");
      setTimeout(() => navigate(`/events/${event.slug || eventId}`), 1200);
    } catch (e) {
      toast(e.message || t.sentFail, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !form) {
    return (
      <div style={{ minHeight: "60vh", display: "flex", alignItems: "center",
        justifyContent: "center", color: C.muted, background: C.bg }}>
        Loading…
      </div>
    );
  }

  // ── UI building blocks ───────────────────────────────────────────────────
  const inputStyle = {
    width: "100%", padding: "11px 14px", borderRadius: 10,
    border: `1px solid ${C.border}`, background: C.inputBg, color: C.fg,
    fontSize: 14, outline: "none", fontFamily: "inherit",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6, display: "block" };
  const sectionStyle = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
    padding: "20px 22px", marginBottom: 16,
  };
  const sectionTitle = {
    fontSize: 13, fontWeight: 800, color: C.accent,
    textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 14,
    display: "flex", alignItems: "center", gap: 8,
  };
  const fieldChanged = (name) => name in diff;
  const changedDot = (name) => fieldChanged(name) ? (
    <span title="Changed" style={{
      display: "inline-block", width: 8, height: 8, borderRadius: 99,
      background: C.accent, marginLeft: 6, verticalAlign: "middle",
    }} />
  ) : null;

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, color: C.fg,
      padding: "32px 16px 80px", fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ maxWidth: 920, margin: "0 auto" }}>

        {/* Header — намеренно отличается от create */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
            padding: "5px 12px", borderRadius: 99,
            background: `${C.accent}22`, color: C.accent,
            fontSize: 11, fontWeight: 800, letterSpacing: ".09em",
            textTransform: "uppercase", marginBottom: 12 }}>
            ✎ Edit Request
          </div>
          <h1 style={{ fontSize: "2rem", fontWeight: 800, margin: "0 0 6px" }}>
            {t.header}
          </h1>
          <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.5 }}>{t.subheader}</div>
        </div>

        {/* Pending banner */}
        {active && (
          <div style={{
            background: C.warn, border: `1px solid ${C.warnBorder}55`,
            borderRadius: 12, padding: "14px 18px", marginBottom: 18,
            display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: 200, fontSize: 13, lineHeight: 1.5 }}>
              <strong style={{ color: C.warnBorder }}>⏳ </strong>{t.pendingBanner}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                {active.created_at && new Date(active.created_at).toLocaleString()}
                {" · "}{Object.keys(active.changes || {}).length} field(s)
                {active.has_new_cover ? " + cover" : ""}
              </div>
            </div>
            <button onClick={onWithdrawPending} type="button"
              style={{
                background: "transparent", border: `1px solid ${C.warnBorder}`,
                color: C.warnBorder, padding: "7px 14px", borderRadius: 8,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}>
              {t.cancelPending}
            </button>
          </div>
        )}

        <form onSubmit={onSubmit}>
          {/* BASIC */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>📝 {t.sectionBasic}</div>
            <label style={labelStyle}>{t.title}{changedDot("title")}</label>
            <input style={inputStyle} value={form.title}
              onChange={(e) => setField("title", e.target.value)} />
            <div style={{ height: 14 }} />
            <label style={labelStyle}>{t.description}{changedDot("description")}</label>
            <textarea rows={5} style={{ ...inputStyle, resize: "vertical" }}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)} />
          </div>

          {/* WHEN */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>📅 {t.sectionWhen}</div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>{t.date}{changedDot("event_date")}</label>
                <input type="date" style={inputStyle} value={form.event_date}
                  onChange={(e) => setField("event_date", e.target.value)} />
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>{t.time}{changedDot("event_time")}</label>
                <input type="time" style={inputStyle} value={form.event_time}
                  onChange={(e) => setField("event_time", e.target.value)} />
              </div>
            </div>
          </div>

          {/* WHERE */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>📍 {t.sectionWhere}</div>
            <label style={labelStyle}>
              {t.location}{changedDot("location")}{changedDot("latitude")}{changedDot("longitude")}
            </label>
            <div style={{ position: "relative" }}>
              <input ref={locationInputRef} style={inputStyle}
                value={form.location}
                placeholder={t.locationPlaceholder}
                onChange={(e) => {
                  setField("location", e.target.value);
                  setIsAddressSelected(false);
                }} />
              {showSuggestions && suggestions.length > 0 && (
                <div ref={suggestionsRef} style={{
                  position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                  background: C.card, border: `1px solid ${C.border}`,
                  borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  maxHeight: 260, overflowY: "auto", zIndex: 10,
                }}>
                  {suggestions.map((s) => (
                    <div key={s.place_id}
                      onClick={() => onLocationSelect(s)}
                      style={{
                        padding: "10px 14px", cursor: "pointer", fontSize: 13,
                        borderBottom: `1px solid ${C.border}`, color: C.fg,
                      }}>
                      {s.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {position && (
              <div style={{ marginTop: 14, height: 260, borderRadius: 12, overflow: "hidden",
                border: `1px solid ${C.border}` }}>
                <Map
                  ref={mapRef}
                  initialViewState={{
                    latitude: position.latitude,
                    longitude: position.longitude,
                    zoom: 16,
                  }}
                  mapStyle={isLight
                    ? "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
                    : "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"}
                  onClick={onMapClick}
                >
                  <NavigationControl position="top-right" />
                  <Marker latitude={position.latitude} longitude={position.longitude} draggable
                    onDragEnd={(e) => {
                      const pos = { latitude: e.lngLat.lat, longitude: e.lngLat.lng };
                      setPosition(pos);
                    }}
                  />
                </Map>
              </div>
            )}
          </div>

          {/* COVER */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>
              🖼 {t.sectionCover}
              {coverFile && (
                <span title="Changed" style={{
                  display: "inline-block", width: 8, height: 8, borderRadius: 99,
                  background: C.accent, marginLeft: 6, verticalAlign: "middle",
                }} />
              )}
            </div>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              {coverPreview && (
                <div style={{ width: 220, flexShrink: 0 }}>
                  <img src={coverPreview} alt="cover"
                    style={{ width: "100%", borderRadius: 10, display: "block",
                      border: `1px solid ${C.border}` }} />
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                    {coverFile ? t.newCover : t.currentCover}
                  </div>
                </div>
              )}
              <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{
                  display: "inline-flex", justifyContent: "center",
                  padding: "11px 16px", borderRadius: 10,
                  background: C.primary, color: "#fff", cursor: "pointer",
                  fontWeight: 700, fontSize: 13, border: "none",
                }}>
                  {t.chooseCover}
                  <input type="file" accept="image/*" onChange={onCoverChoose}
                    style={{ display: "none" }} />
                </label>
                {coverFile && (
                  <button type="button" onClick={onRemoveNewCover}
                    style={{
                      padding: "9px 14px", borderRadius: 10, background: "transparent",
                      border: `1px solid ${C.border}`, color: C.muted, cursor: "pointer",
                      fontSize: 12, fontWeight: 600,
                    }}>
                    {t.removeNewCover}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* PRICE */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>💶 {t.sectionPrice}</div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ flex: "1 1 160px" }}>
                <label style={labelStyle}>{t.ticketPrice}{changedDot("ticket_price")}</label>
                <input type="number" min="0" step="0.01" disabled={form.is_free}
                  style={{ ...inputStyle, opacity: form.is_free ? 0.5 : 1 }}
                  value={form.is_free ? "0" : form.ticket_price}
                  onChange={(e) => setField("ticket_price", e.target.value)} />
              </div>
              <div style={{ flex: "0 0 110px" }}>
                <label style={labelStyle}>{t.currency}{changedDot("currency")}</label>
                <select style={inputStyle} value={form.currency}
                  disabled={form.is_free}
                  onChange={(e) => setField("currency", e.target.value)}>
                  {["EUR", "USD", "INR", "GBP", "USDT"].map(c =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8,
                cursor: "pointer", padding: "11px 14px", fontSize: 13 }}>
                <input type="checkbox" checked={form.is_free}
                  onChange={(e) => setForm(p => ({
                    ...p, is_free: e.target.checked,
                    ticket_price: e.target.checked ? "0" : p.ticket_price,
                  }))} />
                {t.free}
              </label>
              <div style={{ flex: "1 1 160px" }}>
                <label style={labelStyle}>{t.capacity}{changedDot("capacity")}</label>
                <input type="number" min="0" style={inputStyle} value={form.capacity}
                  onChange={(e) => setField("capacity", e.target.value)} />
              </div>
            </div>
          </div>

          {/* SETTINGS */}
          <div style={sectionStyle}>
            <div style={sectionTitle}>⚙ {t.sectionSettings}</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 12, marginBottom: 18 }}>
              {[
                ["is_private",                t.isPrivate],
                ["allow_user_invitations",    t.allowInvites],
                ["allow_external_matching",   t.allowMatching],
                ["require_participant_phone", t.requirePhone],
              ].map(([k, label]) => (
                <label key={k} style={{ display: "flex", alignItems: "center", gap: 10,
                  cursor: "pointer", padding: "10px 12px", borderRadius: 8,
                  background: C.inputBg, border: `1px solid ${C.border}`, fontSize: 13 }}>
                  <input type="checkbox" checked={!!form[k]}
                    onChange={(e) => setField(k, e.target.checked)} />
                  {label}{changedDot(k)}
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>{t.organizerName}{changedDot("organizer_name")}</label>
                <input style={inputStyle} value={form.organizer_name}
                  onChange={(e) => setField("organizer_name", e.target.value)} />
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>{t.organizerContact}{changedDot("organizer_contact")}</label>
                <input style={inputStyle} value={form.organizer_contact}
                  onChange={(e) => setField("organizer_contact", e.target.value)} />
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>{t.organizerWebsite}{changedDot("organizer_website")}</label>
                <input style={inputStyle} value={form.organizer_website}
                  onChange={(e) => setField("organizer_website", e.target.value)} />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div style={{
            display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
            position: "sticky", bottom: 16, background: C.card,
            border: `1px solid ${C.border}`, borderRadius: 14,
            padding: "14px 18px", boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}>
            <div style={{ flex: 1, minWidth: 140, fontSize: 13 }}>
              <span style={{ color: C.muted }}>Pending changes: </span>
              <strong style={{ color: changedFieldsCount > 0 ? C.accent : C.muted }}>
                {changedFieldsCount}
              </strong>
            </div>
            <button type="button"
              onClick={() => navigate(`/events/${event.slug || eventId}`)}
              style={{ background: "transparent", border: `1px solid ${C.border}`,
                color: C.fg, padding: "11px 18px", borderRadius: 10, fontWeight: 600,
                fontSize: 13, cursor: "pointer" }}>
              {t.cancel}
            </button>
            <button type="submit" disabled={submitting || changedFieldsCount === 0}
              style={{
                background: changedFieldsCount === 0 ? C.muted : C.primary,
                color: "#fff", border: "none", borderRadius: 10,
                padding: "11px 22px", fontWeight: 700, fontSize: 13,
                cursor: submitting || changedFieldsCount === 0 ? "not-allowed" : "pointer",
                opacity: submitting ? 0.6 : 1,
              }}>
              {submitting ? t.submitting : t.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
